'use client';

import React, { JSX } from 'react';
import { type DialogProps } from '@radix-ui/react-dialog';
import { toast } from 'sonner';

import { ServerActionResult, type Chat } from '@/lib/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';

import { IconSpinner } from '@/components/ui/icons';
import { useCopyToClipboard } from '@/lib/hooks/use-copy-to-clipboard';

interface ChatShareDialogProps extends DialogProps {
  /**
   * The chat object containing its id, title, messages, and optionally a sharePath.
   */
  chat: Pick<Chat, 'id' | 'title' | 'messages'> & { sharePath?: string };
  /**
   * Function to generate a shareable link for the chat.
   *
   * @param id - The id of the chat to share.
   * @returns A ServerActionResult containing the shared chat details.
   */
  shareChat: (id: string) => ServerActionResult<Chat>;
  /**
   * Function to disable sharing for the chat.
   *
   * @param id - The id of the chat to disable sharing.
   * @returns A ServerActionResult containing the updated chat details.
   */
  disableSharedChat: (id: string) => ServerActionResult<Chat>;
  /**
   * Callback invoked after successfully copying or disabling the share link.
   */
  onCopy: () => void;
}

/**
 * ChatShareDialog component renders a dialog interface to share a chat link.
 *
 * The flow is decoupled into two steps:
 * 1. Generate the share link (if not already available).
 * 2. Copy the generated link to the clipboard.
 *
 * When a share link is available (either via the chat prop or generated),
 * the UI shows the "Disable Shared Chat" and "Copy link" buttons.
 *
 * @param {ChatShareDialogProps} props - Properties to configure the share dialog.
 * @returns {JSX.Element} The rendered ChatShareDialog component.
 */
export function ChatShareDialog({ chat, shareChat, disableSharedChat, onCopy, ...props }: ChatShareDialogProps) {
  // Defensive: skip rendering if chat or chat.id is missing
  if (!chat?.id) return null;
  const { copyToClipboard } = useCopyToClipboard({ timeout: 3000 });
  const [isGenerating, setIsGenerating] = React.useState<boolean>(false);
  const [isDisabling, setIsDisabling] = React.useState<boolean>(false);
  const [shareLink, setShareLink] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (chat.sharePath && !shareLink) {
      const url = new URL(window.location.href);
      url.pathname = chat.sharePath;
      setShareLink(url.toString());
    }
  }, [chat.sharePath, shareLink]);

  /**
   * Generates the share link for the chat.
   */
  const generateShareLink = async () => {
    setIsGenerating(true);
    try {
      const result = await shareChat(chat.id);
      if (result && 'error' in result) {
        toast.error(`Failed to generate share link: ${result.error || 'Unknown error'}`);
        setIsGenerating(false);
        return;
      }
      const sharePath = result.sharePath || chat.sharePath;
      if (!sharePath) {
        toast.error('No share path found. Make sure the chat is shared.');
        setIsGenerating(false);
        return;
      }
      const url = new URL(window.location.href);
      url.pathname = sharePath;
      setShareLink(url.toString());
      toast.success('Share link generated');
    } catch (error) {
      console.error('generateShareLink - Exception occurred:', error);
      toast.error(
        `Error generating share link: ${
          error instanceof Error ? error.message : JSON.stringify(error)
        }`
      );
    }
    setIsGenerating(false);
  };

  /**
   * Handles copying the share link to the clipboard.
   * This onClick handler is invoked directly from the button, preserving the user gesture.
   */
  const handleCopyLink = async () => {
    if (!shareLink) {
      toast.error('No share link to copy');
      return;
    }
    try {
      const copied = await copyToClipboard(shareLink);
      if (copied) {
        onCopy();
        toast.success('Share link copied to clipboard');
      } else {
        toast.error('Failed to copy share link to clipboard: Unknown error');
      }
    } catch (error) {
      console.error('handleCopyLink - Exception occurred:', error);
      toast.error(
        `Failed to copy share link to clipboard: ${
          error instanceof Error ? error.message : JSON.stringify(error)
        }`
      );
    }
  };

  /**
   * Disables the currently shared chat link.
   */
  const handleDisableShareLink = async () => {
    setIsDisabling(true);
    try {
      const result = await disableSharedChat(chat.id);
      if (result && 'error' in result) {
        toast.error(`Failed to disable share link: ${result.error || 'Unknown error'}`);
        setIsDisabling(false);
        return;
      }
      onCopy();
      toast.success('Share link disabled');
      setShareLink(null);
    } catch (error) {
      console.error('handleDisableShareLink - Exception occurred:', error);
      toast.error(
        `Failed to disable share link: ${
          error instanceof Error ? error.message : JSON.stringify(error)
        }`
      );
    }
    setIsDisabling(false);
  };

  return (
    <Dialog {...props}>
      <DialogContent className="generic-dialog-content" aria-describedby="dialog-description">
        <DialogHeader>
          <DialogTitle>Share link to chat</DialogTitle>
          <DialogDescription>
            Anyone with the URL will be able to view the shared chat.
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm w-full box-border overflow-wrap break-words rounded-md">
          <div className="font-medium">Chat: <strong>{chat.title}</strong></div>
          {/* <div className="text-muted-foreground">{chat.messages.length} messages</div> */}
        </div>
        <DialogFooter>
          {shareLink ? (
            <>
              <Button
                variant="secondary"
                onClick={handleDisableShareLink}
                disabled={isDisabling}
                className="hover:underline hover:text-primary cursor-pointer"
                title="Disable the shared chat link"
                style={{ cursor: 'pointer' }}
              >
                {isDisabling ? (
                  <>
                    <IconSpinner className="mr-2 animate-spin" />
                    Disabling...
                  </>
                ) : (
                  <>Disable Shared Chat</>
                )}
              </Button>
              <Button
                onClick={handleCopyLink}
                className="hover:underline hover:text-primary cursor-pointer"
                title="Copy the share link to clipboard"
                style={{ cursor: 'pointer' }}
              >
                Copy link
              </Button>
            </>
          ) : (
            <Button
              disabled={isGenerating}
              onClick={generateShareLink}
              title="Generate a shareable link for this chat"
            >
              {isGenerating ? (
                <>
                  <IconSpinner className="mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>Generate Share Link</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}